<template>
    <div class="event-ticketing">
        <div class="event-ticketing__card">
            <h3 class="event-ticketing__card-title">{{ langJson('Ticket Types') }}</h3>

            <div :class="['event-ticketing__field', locked('currency') && 'event-ticketing--locked']">
                <div class="event-ticketing__label">{{ langJson('Currency') }} <span class="event-ticketing__req">*</span></div>
                <div class="event-ticketing__pills">
                    <button v-for="code in currencies" :key="code" type="button" :class="['event-ticketing__pill', settings.currency === code && 'event-ticketing__pill--active']" @click="settings.currency = code">{{ code }}</button>
                </div>
            </div>

            <div :class="['event-ticketing__field', locked('transferable') && 'event-ticketing--locked']">
                <div class="event-ticketing__label">{{ langJson('Transferability') }}</div>
                <div class="event-ticketing__pills">
                    <button type="button" :class="['event-ticketing__pill', settings.transferable && 'event-ticketing__pill--active']" @click="settings.transferable = true">{{ langJson('Yes') }}</button>
                    <button type="button" :class="['event-ticketing__pill', !settings.transferable && 'event-ticketing__pill--active']" @click="settings.transferable = false">{{ langJson('No') }}</button>
                </div>
                <p class="event-ticketing__hint">{{ langJson(settings.transferable ? 'Holders can send or resell their ticket NFT.' : 'Ticket NFTs are locked to the buyer’s wallet.') }}</p>
            </div>

            <div class="event-ticketing__row">
                <div :class="['event-ticketing__field', !tiersEditable && 'event-ticketing--locked']">
                    <div class="event-ticketing__label">{{ langJson('Number of Categories') }} <span class="event-ticketing__req">*</span></div>
                    <el-input-number v-model="categoryCount" class="event-ticketing__number" :min="1" :max="20" :disabled="!tiersEditable" controls-position="right"></el-input-number>
                </div>
                <div :class="['event-ticketing__field', locked('maxCapacity') && 'event-ticketing--locked']">
                    <div class="event-ticketing__label">{{ langJson('Max capacity') }} <span class="event-ticketing__optional">{{ langJson('(optional)') }}</span></div>
                    <el-input v-model="settings.maxCapacity" class="event-ticketing__input" type="number" min="1" :placeholder="langJson('e.g. venue capacity')"></el-input>
                    <p class="event-ticketing__hint">{{ langJson('Total tickets that can ever exist. Leave blank to use the sum of your categories; set it higher to add tickets after launch.') }}</p>
                </div>
            </div>
        </div>

        <div :class="['event-ticketing__card', locked('seatmapImageUrl') && 'event-ticketing--locked']">
            <h3 class="event-ticketing__card-title">{{ langJson('Seatmap') }} <span class="event-ticketing__optional">{{ langJson('(optional)') }}</span></h3>
            <FormItem v-model="settings.seatmapImageUrl" type="image" :uploadTip="langJson('(Acceptable file format: JPEG, JPG, PNG.)')"></FormItem>
        </div>

        <p v-if="liveMode" class="event-ticketing__live-note">{{ langJson('Tickets are on sale: you can add more tickets to a category, and change its description and artwork. Names and prices are fixed.') }}</p>
        <div v-for="(tier, index) in tiers" :key="tier.key" :class="['event-ticketing__card', 'event-ticketing__category', !tiersEditable && !liveMode && 'event-ticketing--locked']">
            <div class="event-ticketing__category-head" @click="tier.open = !tier.open">
                <h3>{{ langJson('Category') }} {{ index + 1 }}</h3>
                <span class="event-ticketing__category-name">{{ tier.name }}</span>
                <span v-if="tier.price !== '' && tier.price != null" class="event-ticketing__category-price">{{ settings.currency }} {{ Number(tier.price).toFixed(2) }}</span>
                <span v-if="!tierValid(tier)" class="event-ticketing__category-todo">{{ langJson('Incomplete') }}</span>
                <span class="event-ticketing__chevron">{{ tier.open ? '⌃' : '⌄' }}</span>
            </div>
            <div v-show="tier.open" class="event-ticketing__category-body">
                <div :class="liveMode && 'event-ticketing--locked'">
                    <FormItem v-model="tier.name" type="text" :matchs="['unull']" :title="langJson('Title')" :placeholder="langJson('e.g. Gold Tier')"></FormItem>
                </div>
                <FormItem v-model="tier.description" type="textarea" height="140px" :title="langJson('Description')" :placeholder="langJson('One perk per line')"></FormItem>
                <div class="event-ticketing__row event-ticketing__row--3">
                    <div :class="['event-ticketing__field', liveMode && 'event-ticketing--locked']">
                        <div class="event-ticketing__label">{{ langJson('Amount') }} <span class="event-ticketing__req">*</span></div>
                        <el-input v-model="tier.price" class="event-ticketing__input" type="number" min="0" step="0.01" placeholder="0.00">
                            <template slot="prepend">{{ settings.currency || '—' }}</template>
                        </el-input>
                    </div>
                    <div class="event-ticketing__field">
                        <div class="event-ticketing__label">{{ langJson('Quantity available') }} <span class="event-ticketing__req">*</span></div>
                        <el-input v-model="tier.quantityTotal" class="event-ticketing__input" type="number" :min="liveMode ? tier.liveMinimum : 1" placeholder="100"></el-input>
                        <p v-if="liveMode" class="event-ticketing__hint">{{ tier.sold }} {{ langJson('sold') }} · {{ langJson('add only') }}</p>
                    </div>
                    <div :class="['event-ticketing__field', liveMode && 'event-ticketing--locked']">
                        <div class="event-ticketing__label">{{ langJson('Max per order') }} <span class="event-ticketing__req">*</span></div>
                        <el-input v-model="tier.maxPerOrder" class="event-ticketing__input" type="number" min="1" max="50" placeholder="4"></el-input>
                    </div>
                </div>
                <div :class="['event-ticketing__field', liveMode && 'event-ticketing--locked']">
                    <div class="event-ticketing__label">{{ langJson('Category sales window') }} <span class="event-ticketing__optional">{{ langJson('(optional — e.g. early bird)') }}</span></div>
                    <el-date-picker v-model="tier.saleDates" class="event-ticketing__picker" type="datetimerange" format="dd/MM/yy HH:mm" :start-placeholder="langJson('Sales open')" :end-placeholder="langJson('Sales close')" range-separator="–"></el-date-picker>
                </div>
                <div class="event-ticketing__field">
                    <div class="event-ticketing__label">{{ langJson('NFT artwork') }} <span class="event-ticketing__optional">{{ langJson('(optional — each ticket gets one of these; defaults to the event banner)') }}</span></div>
                    <div class="event-ticketing__art">
                        <div v-for="(art, i) in tier.nftImages" :key="i" class="event-ticketing__art-item">
                            <img v-if="art.image" :src="art.image" alt="" />
                            <FormItem v-else v-model="art.image" type="image" :uploadTip="langJson('JPEG, PNG')"></FormItem>
                            <button type="button" class="event-ticketing__art-remove" @click="tier.nftImages.splice(i, 1)">×</button>
                        </div>
                        <button v-if="tier.nftImages.length < 10" type="button" class="event-ticketing__art-add" @click="tier.nftImages.push({ image: '' })">+ {{ langJson('Add artwork') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage, utcToWallClock, wallClockToUtc } from '@/assets/js/organizerEvent'

const AUTOSAVE_DELAY = 800
const CURRENCIES = ['SGD', 'USD', 'EUR']
let keySeq = 0

function blankTier() {
    return { key: ++keySeq, id: null, name: '', description: '', price: '', quantityTotal: '', maxPerOrder: 4, saleDates: null, nftImages: [], open: true }
}

/**
 * Ticketing step. Two independent autosaves:
 *  - event-level settings (currency, transferable, capacity, seatmap) → PATCH of changed fields
 *  - categories → PUT of the whole ordered list, sent only once every category is complete
 * Emits `status` and `saved` like EventBasicsForm.
 */
export default {
    props: {
        event: { type: Object, required: true },
    },
    data() {
        const tiers = (this.event.tiers || []).map((t) => this.tierFromApi(t))
        return {
            currencies: CURRENCIES,
            settings: {
                currency: this.event.currency || '',
                transferable: this.event.transferable !== false,
                maxCapacity: this.event.maxCapacity == null ? '' : String(this.event.maxCapacity),
                seatmapImageUrl: this.event.seatmapImageUrl || '',
            },
            tiers: tiers.length ? tiers : [blankTier()],
            lastSettings: null,
            lastTiers: null,
            settingsTimer: null,
            tiersTimer: null,
            inFlight: null,
        }
    },
    computed: {
        tiersEditable() {
            return this.event.tiersEditable !== false
        },
        liveMode() {
            return this.event.status === 'live'
        },
        timezone() {
            return this.event.timezone || undefined
        },
        categoryCount: {
            get() {
                return this.tiers.length
            },
            set(count) {
                while (this.tiers.length < count) this.tiers.push(blankTier())
                if (this.tiers.length > count) this.tiers.splice(count)
            },
        },
    },
    created() {
        this.lastSettings = this.settingsPayload()
        this.lastTiers = JSON.stringify(this.tiersPayload())
        this.liveSaved = Object.fromEntries(this.tiers.filter((t) => t.id).map((t) => [t.id, this.livePayload(t)]))
    },
    beforeDestroy() {
        this.flush()
    },
    watch: {
        settings: {
            deep: true,
            handler() {
                clearTimeout(this.settingsTimer)
                this.settingsTimer = setTimeout(() => this.saveSettings(), AUTOSAVE_DELAY)
            },
        },
        tiers: {
            deep: true,
            handler() {
                clearTimeout(this.tiersTimer)
                this.tiersTimer = setTimeout(() => this.saveTiers(), AUTOSAVE_DELAY)
            },
        },
        // Categories filled in before a currency was picked were held back by saveTiers;
        // retry them now or they're lost on reload while the page says "saved".
        'settings.currency'() {
            clearTimeout(this.tiersTimer)
            this.tiersTimer = setTimeout(() => this.saveTiers(), AUTOSAVE_DELAY)
        },
    },
    methods: {
        tierFromApi(t) {
            return {
                key: ++keySeq,
                id: t.id,
                name: t.name || '',
                description: t.description || '',
                price: t.price == null ? '' : String(Number(t.price)),
                quantityTotal: String(t.quantityTotal),
                maxPerOrder: String(t.maxPerOrder),
                saleDates: t.saleStart && t.saleEnd ? [utcToWallClock(t.saleStart, this.event.timezone), utcToWallClock(t.saleEnd, this.event.timezone)] : null,
                nftImages: (t.nftImages || []).map((n) => ({ ...n })),
                sold: t.quantitySold || 0,
                liveMinimum: t.quantityTotal,
                open: false,
            }
        },
        locked(field) {
            return !(this.event.editableFields || []).includes(field)
        },
        settingsPayload() {
            const { currency, transferable, maxCapacity, seatmapImageUrl } = this.settings
            return {
                currency: currency || null,
                transferable,
                maxCapacity: maxCapacity === '' || maxCapacity == null ? null : Number(maxCapacity),
                seatmapImageUrl: seatmapImageUrl || null,
            }
        },
        tierValid(t) {
            const price = Number(t.price)
            const qty = Number(t.quantityTotal)
            const max = Number(t.maxPerOrder)
            return Boolean(t.name.trim()) && t.price !== '' && price >= 0 && Number.isInteger(qty) && qty >= 1 && Number.isInteger(max) && max >= 1 && max <= qty
        },
        tiersPayload() {
            const tz = this.event.timezone
            return this.tiers.map((t) => ({
                ...(t.id ? { id: t.id } : {}),
                name: t.name.trim(),
                description: t.description.trim() || null,
                price: Number(t.price),
                quantityTotal: Number(t.quantityTotal),
                maxPerOrder: Number(t.maxPerOrder),
                saleStart: t.saleDates ? wallClockToUtc(t.saleDates[0], tz).toISOString() : null,
                saleEnd: t.saleDates ? wallClockToUtc(t.saleDates[1], tz).toISOString() : null,
                nftImages: t.nftImages.filter((n) => n.image),
            }))
        },
        flush() {
            clearTimeout(this.settingsTimer)
            clearTimeout(this.tiersTimer)
            return Promise.all([this.saveSettings(), this.saveTiers()])
        },
        async serial(task) {
            // One request at a time so an older response never lands after a newer one.
            while (this.inFlight) await this.inFlight.catch(() => {})
            this.inFlight = task()
            try {
                return await this.inFlight
            } finally {
                this.inFlight = null
            }
        },
        async saveSettings() {
            const next = this.settingsPayload()
            const diff = {}
            Object.keys(next).forEach((key) => {
                if (next[key] !== this.lastSettings[key] && !this.locked(key)) diff[key] = next[key]
            })
            if (!Object.keys(diff).length) return
            this.$emit('status', 'saving')
            try {
                const res = await this.serial(() => organizerApi.updateEvent(this.event.id, diff))
                Object.assign(this.lastSettings, diff)
                this.$emit('saved', res.data)
                this.$emit('status', 'saved')
            } catch (err) {
                this.$emit('status', 'error', apiErrorMessage(err, this.langJson('Changes not saved')))
            }
        },
        livePayload(t) {
            return {
                description: t.description.trim() || null,
                quantityTotal: Number(t.quantityTotal),
                nftImages: t.nftImages.filter((n) => n.image),
            }
        },
        // Live events: PATCH each changed category on its own (description, more stock, artwork).
        async saveLiveTiers() {
            for (const tier of this.tiers.filter((t) => t.id)) {
                const next = this.livePayload(tier)
                const prev = this.liveSaved[tier.id]
                const diff = {}
                Object.keys(next).forEach((key) => {
                    if (JSON.stringify(next[key]) !== JSON.stringify(prev[key])) diff[key] = next[key]
                })
                if (!Object.keys(diff).length) continue
                if (diff.quantityTotal !== undefined && !(diff.quantityTotal >= tier.liveMinimum)) {
                    this.$emit('status', 'error', `${tier.name}: ${this.langJson('tickets can only be added once on sale')} (${tier.liveMinimum}+)`)
                    continue
                }
                this.$emit('status', 'saving')
                try {
                    const res = await this.serial(() => organizerApi.patchLiveTier(this.event.id, tier.id, diff))
                    this.liveSaved[tier.id] = { ...prev, ...diff }
                    if (diff.quantityTotal !== undefined) tier.liveMinimum = diff.quantityTotal
                    this.$emit('saved', res.data)
                    this.$emit('status', 'saved')
                } catch (err) {
                    this.$emit('status', 'error', apiErrorMessage(err, this.langJson('Ticket category not saved')))
                }
            }
        },
        async saveTiers() {
            if (this.liveMode) return this.saveLiveTiers()
            if (!this.tiersEditable) return
            const payload = this.tiersPayload()
            const snapshot = JSON.stringify(payload)
            if (snapshot === this.lastTiers) return
            if (!this.tiers.every((t) => this.tierValid(t))) {
                this.$emit('status', 'error', this.langJson('Finish every ticket category (title, amount, quantity, max per order) to save it'))
                return
            }
            if (!this.settings.currency) {
                this.$emit('status', 'error', this.langJson('Choose a currency to save your ticket categories'))
                return
            }
            // The API prices tiers in the event's currency, so it must be saved first.
            await this.saveSettings()
            this.$emit('status', 'saving')
            try {
                const res = await this.serial(() => organizerApi.replaceTiers(this.event.id, payload))
                // New categories now have ids — the API returns them in display order.
                const saved = res.data.tiers || []
                const sent = payload.map((p, i) => (p.id || !saved[i] ? p : { ...p, id: saved[i].id }))
                saved.forEach((tier, i) => {
                    if (this.tiers[i] && !this.tiers[i].id) this.tiers[i].id = tier.id
                })
                // Record what was SENT, not the live form: edits made while the request
                // was in flight must still differ from it and get saved by the next tick.
                this.lastTiers = JSON.stringify(sent)
                this.$emit('saved', res.data)
                this.$emit('status', 'saved')
            } catch (err) {
                this.$emit('status', 'error', apiErrorMessage(err, this.langJson('Ticket categories not saved')))
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.event-ticketing {
    display: flex;
    flex-direction: column;
    gap: 24px;

    &__card {
        background: var(--color_white);
        border-radius: 32px;
        padding: 32px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
    &__card-title {
        font-family: 'Inter';
        font-weight: 700;
        font-size: 18px;
        color: var(--font_deep_color);
    }
    &__row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;

        &--3 {
            grid-template-columns: 1fr 1fr 1fr;
        }
    }
    &__field {
        min-width: 0;
    }
    &__label {
        font-family: 'Lato';
        font-size: 14px;
        line-height: 20px;
        color: var(--font_color_black);
        margin-bottom: 8px;
    }
    &__req {
        color: var(--danger_color);
    }
    &__optional {
        color: var(--font_modif_color);
        font-size: 12px;
        font-weight: 400;
    }
    &__hint {
        margin-top: 6px;
        font-family: 'Lato';
        font-size: 12px;
        color: var(--font_modif_color);
    }
    &__pills {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    &__pill {
        border: 1.5px solid transparent;
        background: var(--bg_color);
        color: var(--font_deep_color);
        border-radius: 50px;
        padding: 10px 18px;
        font-family: 'Lato';
        font-size: 14px;
        cursor: pointer;

        &--active {
            background: #f1e9ff;
            border-color: #a855f7;
            font-weight: 700;
        }
    }
    &__input,
    &__number,
    &__picker {
        width: 100% !important;

        ::v-deep .el-input__inner,
        &.el-range-editor {
            border-radius: 50px;
            border: none;
            background: var(--bg_color);
            height: 48px;
        }
        ::v-deep .el-range-input {
            background: transparent;
        }
        ::v-deep .el-input-group__prepend {
            border: none;
            border-radius: 50px 0 0 50px;
            background: var(--bg_color);
        }
    }
    &__category {
        gap: 16px;
    }
    &__live-note {
        padding: 16px 24px;
        border-radius: 24px;
        background: #f1e9ff;
        color: #6d28d9;
        font-family: 'Lato';
        font-size: 14px;
    }
    &__category-head {
        display: flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;

        h3 {
            font-family: 'Inter';
            font-weight: 700;
            font-size: 16px;
            color: var(--font_deep_color);
            white-space: nowrap;
        }
    }
    &__category-name {
        font-family: 'Lato';
        font-size: 16px;
        color: var(--font_deep_color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    &__category-price {
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_modif_color);
        white-space: nowrap;
    }
    &__category-todo {
        font-family: 'Lato';
        font-size: 12px;
        color: #b54708;
        background: #fff6e5;
        padding: 2px 10px;
        border-radius: 50px;
    }
    &__chevron {
        margin-left: auto;
        font-size: 18px;
        color: var(--font_modif_color);
    }
    &__category-body {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
    &__art {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
    &__art-item {
        position: relative;
        width: 140px;

        img {
            width: 140px;
            height: 140px;
            object-fit: cover;
            border-radius: 16px;
            display: block;
        }
    }
    &__art-remove {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: none;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        cursor: pointer;
    }
    &__art-add {
        width: 140px;
        height: 140px;
        border-radius: 16px;
        border: 1.5px dashed #a855f7;
        background: transparent;
        color: #a855f7;
        font-family: 'Lato';
        cursor: pointer;
    }

    &--locked {
        opacity: 0.55;
        pointer-events: none;
    }
}

@media only screen and (max-width: 1080px) {
    .event-ticketing {
        gap: 16px;

        &__card {
            padding: 20px 16px;
            border-radius: 24px;
        }
        &__row,
        &__row--3 {
            grid-template-columns: 1fr;
        }
    }
}
</style>
